import { ProjectTemperatureData } from '../models/project-temperature-data';

/**
 * Resolution of the RT poll cursor (`lastEpocTime`).
 *
 * Background
 * ----------
 * Polls are cursor-based: `GetTemperatureData?isRealtime=true&lastEpocTime=N`
 * returns the rows after epoch N. The cursor used to be derived client-side as
 * `data.TRC[0].E` — the FIRST row of the response — which is only correct when a
 * response carries exactly one row. With two or more rows it either re-requests
 * rows already seen (ascending order) or skips the ones after index 0
 * (descending), and the historical backfill never seeded it at all, so the very
 * first live poll re-fetched the whole cure.
 *
 * Telemetry-pipeline item 2 added a server-computed high-water mark (`HWM`) to
 * the temperature/zone/loss responses. When present it is authoritative: it is the
 * exact epoch the next poll should resume from, which makes the backfill -> live
 * handoff gap-free and duplicate-free end to end.
 *
 * Backward compatibility
 * ----------------------
 * `HWM` is optional. Against an API build that does not send it (or sends 0) this
 * falls back to the previous derivation, so an old server keeps working exactly as
 * before.
 */

/** Largest epoch present in the response rows, or 0 when there are none. */
export function maxEpochOf(data: Pick<ProjectTemperatureData, 'TRC'> | null | undefined): number {
  const rows = data?.TRC;
  if (!rows || rows.length === 0) {
    return 0;
  }
  let max = 0;
  for (const row of rows) {
    const e = Number(row?.E);
    if (Number.isFinite(e) && e > max) {
      max = e;
    }
  }
  return max;
}

/**
 * Next value for `lastEpocTime`.
 *
 * Preference order:
 *   1. `HWM` from the response (server-authoritative).
 *   2. The largest row epoch in the response (correct for multi-row responses,
 *      unlike the old `TRC[0].E`).
 *   3. The current cursor — never regress.
 *
 * The cursor is monotonic by construction: a stale or malformed value can never
 * move it backwards, because moving backwards means re-delivering ticks the client
 * has already ingested.
 *
 * @param data     the poll or backfill response
 * @param current  the cursor in force right now
 * @returns the cursor to send on the next poll
 */
export function resolveNextCursor(
  data: Pick<ProjectTemperatureData, 'TRC' | 'HWM'> | null | undefined,
  current: number
): number {
  const safeCurrent = Number.isFinite(current) && current > 0 ? current : 0;

  const hwm = Number(data?.HWM);
  if (Number.isFinite(hwm) && hwm > 0) {
    return Math.max(hwm, safeCurrent);
  }

  // Legacy path: no HWM from this API build.
  const fallback = maxEpochOf(data);
  return fallback > safeCurrent ? fallback : safeCurrent;
}

/**
 * The newest row in the response, by epoch — not `TRC[0]`, whose meaning depends
 * on the server's sort order. Used for the "current time" readout so it matches
 * the newest tick actually ingested.
 */
export function newestRowOf(
  data: Pick<ProjectTemperatureData, 'TRC'> | null | undefined
) {
  const rows = data?.TRC;
  if (!rows || rows.length === 0) {
    return undefined;
  }
  let newest = rows[0];
  for (const row of rows) {
    const e = Number(row?.E);
    if (Number.isFinite(e) && e > Number(newest?.E ?? 0)) {
      newest = row;
    }
  }
  return newest;
}

/** True when the response carried a usable server high-water mark. */
export function hasHighWaterMark(
  data: Pick<ProjectTemperatureData, 'HWM'> | null | undefined
): boolean {
  const hwm = Number(data?.HWM);
  return Number.isFinite(hwm) && hwm > 0;
}

// ---------------------------------------------------------------------------
// Per-family cursors
// ---------------------------------------------------------------------------

/**
 * The three telemetry families, each with its OWN poll endpoint and its own
 * server-side high-water mark.
 */
export type TelemetryFamily = 'temperature' | 'zone' | 'loss';

export const TELEMETRY_FAMILIES: TelemetryFamily[] = ['temperature', 'zone', 'loss'];

/** Anything carrying an optional server high-water mark. */
export interface HwmBearing {
  HWM?: number;
}

/**
 * Independent poll cursors, one per family.
 *
 * Why independent
 * ---------------
 * The API owner confirmed the three HWMs are computed INDEPENDENTLY and are not
 * guaranteed to advance together. A single shared cursor (previously driven off
 * the temperature response) is therefore a silent-skip bug: once temperature
 * advances the shared cursor to T, a zone or loss row at an epoch <= T that only
 * becomes visible later is never requested again, and is lost for the rest of the
 * cure. Each family now advances only on its own HWM.
 *
 * Legacy fallback
 * ---------------
 * Only the temperature rows carry an epoch (`TemperatureDataRow.E`). ZoneDataRow
 * has just `ZR`, and LossDataRow has no `E` either — so for zone and loss there is
 * no client-derivable cursor at all. Against an API build that predates HWM those
 * families therefore fall back to the TEMPERATURE cursor, which is exactly the old
 * shared-cursor behaviour: no better, but no worse, and no regression on an old
 * server. Once a family reports a usable HWM it becomes independent and never
 * consults the temperature cursor again.
 */
export class TelemetryCursorSet {
  private cursors: Record<TelemetryFamily, number> = {
    temperature: 0,
    zone: 0,
    loss: 0,
  };
  /** Whether this family has ever reported a usable HWM. */
  private independent: Record<TelemetryFamily, boolean> = {
    temperature: false,
    zone: false,
    loss: false,
  };

  /** Clear all cursors — project change. */
  reset(): void {
    for (const f of TELEMETRY_FAMILIES) {
      this.cursors[f] = 0;
      this.independent[f] = false;
    }
  }

  /** True once this family has supplied its own HWM. */
  isIndependent(family: TelemetryFamily): boolean {
    return this.independent[family];
  }

  /** The family's own stored cursor, ignoring any legacy fallback. */
  raw(family: TelemetryFamily): number {
    return this.cursors[family];
  }

  /**
   * The value to send as `lastEpocTime` for this family's next poll.
   *
   * Independent once the family has reported an HWM; otherwise the temperature
   * cursor, preserving legacy shared-cursor behaviour.
   */
  for(family: TelemetryFamily): number {
    if (family === 'temperature' || this.independent[family]) {
      return this.cursors[family];
    }
    return this.cursors.temperature;
  }

  /**
   * Advance a family from its own response.
   *
   * @param family        which endpoint answered
   * @param data          that family's response (its own HWM)
   * @param fallbackEpoch client-derivable epoch for this response, or 0 when the
   *                      family's rows carry none (zone and loss)
   * @returns the family's cursor after the advance
   */
  advance(
    family: TelemetryFamily,
    data: HwmBearing | null | undefined,
    fallbackEpoch = 0
  ): number {
    const current = this.cursors[family];
    const hwm = Number(data?.HWM);

    if (Number.isFinite(hwm) && hwm > 0) {
      this.independent[family] = true;
      this.cursors[family] = Math.max(hwm, current);
      return this.cursors[family];
    }

    // No HWM from this build. Only temperature can derive a cursor from its rows.
    if (fallbackEpoch > current) {
      this.cursors[family] = fallbackEpoch;
    }
    return this.cursors[family];
  }

  /** Snapshot for logging/diagnostics. */
  snapshot(): Record<TelemetryFamily, number> {
    return { ...this.cursors };
  }
}
